import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  getPredefinedComments,
  getReviewAndSubmitStep1Data,
  // getTabCounts,
  setReviewAndSubmitLoading,
} from '../../redux/certdetails/action';
import {
  PERCENTAGE_100,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../utils/constant';
import { useInvertedLocalize } from '../../utils/localization';
import { getMessageKey } from '../../utils/utils';
import {
  calculatePercentage,
  DONE,
  TOTAL,
} from '../CertsDetails/PopoverProgressBreakdown';
import { TAB_STATUS_ALL } from '../ViewControls';

import {
  ACTIONKEY_FOR_PREDEFINED_COMMENTS,
  BUTTON_SIZES,
  STEP1_CATEGORIES,
  TAB_STATUS_CONS,
  TAB_STATUS_TODO,
  TRANSITION_TIMEOUT,
} from './constant';
import invertedMessages from './messages';
import ReviewAndSubmitBody from './ReviewAndSubmitBody';
import ReviewAndSubmitFooter from './ReviewAndSubmitFooter';
import {
  ReviewAndSubmitHeader,
  ReviewAndSubmitSubTitle,
} from './ReviewAndSubmitHeader';

import './ReviewAndSubmit.css';

function ReviewAndSubmit({ buttonSize }) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const {
    bulkAction,
    core: { isLoading, predefinedComments },
    global: { certKey, mediaQueryValue },
    progressBreakdowndetails: { byDecisionDetails },
    reviewAndSubmit,
    tabs: { tabData },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);

  const todoTabCount = tabData?.find(
    (tab) => tab.key === TAB_STATUS_TODO
  )?.count;
  const consultedTabCount = tabData?.find(
    (tab) => tab.key === TAB_STATUS_CONS
  )?.count;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [firstStep, setFirstStep] = useState(true);
  const [dataForSubmitAction, setDataForSubmitAction] = useState({});
  const [isAlertActive, setIsAlertActive] = useState(false);
  const [config, setConfig] = useState(null);
  const [percentage, setPercentage] = useState(0);
  const hasZeroCountForItems = Boolean(
    todoTabCount === 0 && consultedTabCount === 0
  );

  useEffect(() => {
    if (byDecisionDetails && byDecisionDetails?.Summary) {
      setPercentage(
        calculatePercentage(
          byDecisionDetails.Summary[DONE],
          byDecisionDetails.Summary[TOTAL]
        )
      );
    }
  }, [byDecisionDetails]);

  useEffect(() => {
    if (tabData && reviewAndSubmit) {
      setConfig({
        primaryCertifier: reviewAndSubmit?.primaryCertifier,
        isComplete: hasZeroCountForItems,
        withESignature: reviewAndSubmit?.withESignature,
        isCommentEnabled: reviewAndSubmit?.isCommentEnabled,
      });
    }
  }, [tabData, todoTabCount, reviewAndSubmit]);

  useEffect(() => {
    if (certKey) setIsDisabled(false);
  }, [certKey, bulkAction]);

  useEffect(() => {
    const allTabCount = tabData?.find(
      (tab) => tab.key === TAB_STATUS_ALL
    )?.count;

    if (
      isLoading ||
      bulkAction?.isChecked ||
      bulkAction?.isDisabled ||
      bulkAction?.isIndeterminate ||
      bulkAction?.isSubmittingAction ||
      (allTabCount === 0 && reviewAndSubmit.certificationStatus !== 2) // disable only when campaign status is 2 - compelete status
    ) {
      setIsDisabled(true);

      return;
    }

    setIsDisabled(false);
  }, [bulkAction, isLoading, tabData]);

  const closeReviewAndSubmitModal = () => {
    if (!reviewAndSubmit?.isSubmittingReviewAndSubmit) {
      setIsModalOpen(false);

      setTimeout(() => {
        setFirstStep(true);
        dispatch(setReviewAndSubmitLoading(true));
      }, TRANSITION_TIMEOUT);
    }
  };

  const openReviewAndSubmitModal = () => {
    const promisesForStep1Data = [];

    // TODO: Disabling tab count api as tab count is getting refreshed after the action
    // promisesForStep1Data.push(dispatch(getTabCounts(certKey)));

    STEP1_CATEGORIES.forEach((category) => {
      promisesForStep1Data.push(
        dispatch(getReviewAndSubmitStep1Data(certKey, category))
      );
    });

    if (
      !(ACTIONKEY_FOR_PREDEFINED_COMMENTS in predefinedComments) &&
      config?.isCommentEnabled
    )
      promisesForStep1Data.push(
        dispatch(getPredefinedComments(ACTIONKEY_FOR_PREDEFINED_COMMENTS))
      );

    Promise.all(promisesForStep1Data)
      .then(() => {
        dispatch(setReviewAndSubmitLoading(false));
        promisesForStep1Data.length = 0;
      })
      .catch(() => {
        promisesForStep1Data.length = 0;
      });
    setIsModalOpen(true);
  };

  const reviewAndSubmitModal = classnames('ReviewAndSubmitModal');

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileWidth = mediaQueryValue >= mobileXlargeQuery.value;

  const subTitleClassName = classnames(
    'Certs-reviewAndSubmit-mobileDrawerSubTitle',
    !firstStep && 'Certs-reviewAndSubmit-mobileDrawerSubTitle--secondStep'
  );

  const mobileDrawerClassName = classnames(
    'Certs-reviewAndSubmit-mobileDrawer',
    !firstStep && 'Certs-reviewAndSubmit-mobileDrawer--step2'
  );

  return (
    <Box>
      {reviewAndSubmit?.shouldShowReviewAndSubmitButton && (
        <Button
          type='button'
          className='Certs-reviewAndSubmit-btn'
          kind={hasZeroCountForItems || isDisabled ? 'filled' : 'outlined'}
          onClick={openReviewAndSubmitModal}
          size={buttonSize}
          isDisabled={isDisabled}>
          {percentage < PERCENTAGE_100 && (
            <Icon
              kind='ArrowRight'
              className='Certs-reviewAndSubmit-icon'
              color='indigo-700'
            />
          )}
          <Typography
            className={`${
              percentage === PERCENTAGE_100
                ? 'Certs-reviewAndSubmit-showLabel'
                : 'Certs-reviewAndSubmit-label'
            }`}
            kind='body2-bold'>
            {config?.primaryCertifier
              ? messages.CERTS_REVIEW_AND_SUBMIT ||
                localize(intl, getMessageKey('CERTS_REVIEW_AND_SUBMIT'))
              : messages.CERTS_COMPLETE ||
                localize(intl, getMessageKey('CERTS_COMPLETE'))}
          </Typography>
        </Button>
      )}
      {isMobileWidth ? (
        <MobileDrawer
          isOpen={isModalOpen}
          setIsOpen={closeReviewAndSubmitModal}
          headerContent={
            <header className='Certs-reviewAndSubmit-mobileDrawerHeader'>
              <div className='Certs-reviewAndSubmit-mobileDrawerTitleContainer'>
                <Typography
                  kind='h1'
                  className='Certs-reviewAndSubmit-mobileDrawerTitle'>
                  <ReviewAndSubmitHeader
                    firstStep={firstStep}
                    setFirstStep={setFirstStep}
                  />
                </Typography>
                <Typography
                  kind='body2'
                  mobile='body2-m'
                  className={subTitleClassName}>
                  <ReviewAndSubmitSubTitle firstStep={firstStep} />
                </Typography>
              </div>
              <ButtonIcon
                size='medium'
                kind='ghost'
                icon={
                  <Icon kind='close' color='neutral-100' size='smallMedium' />
                }
                onClick={() => {
                  setIsModalOpen(false);
                }}
                ariaLabel={l('Close')}
              />
            </header>
          }
          content={
            <ReviewAndSubmitBody
              firstStep={firstStep}
              isModalOpen={isModalOpen}
              isAlertActive={isAlertActive}
              setIsAlertActive={setIsAlertActive}
              setDataForSubmitAction={setDataForSubmitAction}
              retriggerApisForError={openReviewAndSubmitModal}
              closeReviewAndSubmitModal={closeReviewAndSubmitModal}
            />
          }
          footerActionBar={
            <ReviewAndSubmitFooter
              firstStep={firstStep}
              isModalOpen={isModalOpen}
              setFirstStep={setFirstStep}
              setIsAlertActive={setIsAlertActive}
              dataForSubmitAction={dataForSubmitAction}
              closeReviewAndSubmitModal={closeReviewAndSubmitModal}
            />
          }
          className={mobileDrawerClassName}
        />
      ) : (
        <ModalIsland
          className={reviewAndSubmitModal}
          onClose={closeReviewAndSubmitModal}
          isOpen={isModalOpen}
          title={
            <ReviewAndSubmitHeader
              firstStep={firstStep}
              setFirstStep={setFirstStep}
            />
          }
          subtitle={<ReviewAndSubmitSubTitle firstStep={firstStep} />}
          contentBody={
            <ReviewAndSubmitBody
              firstStep={firstStep}
              isModalOpen={isModalOpen}
              isAlertActive={isAlertActive}
              setIsAlertActive={setIsAlertActive}
              setDataForSubmitAction={setDataForSubmitAction}
              retriggerApisForError={openReviewAndSubmitModal}
              closeReviewAndSubmitModal={closeReviewAndSubmitModal}
            />
          }
          FooterActionBarComp={
            <ReviewAndSubmitFooter
              firstStep={firstStep}
              isModalOpen={isModalOpen}
              setFirstStep={setFirstStep}
              setIsAlertActive={setIsAlertActive}
              dataForSubmitAction={dataForSubmitAction}
              closeReviewAndSubmitModal={closeReviewAndSubmitModal}
            />
          }
        />
      )}
    </Box>
  );
}

ReviewAndSubmit.propTypes = { buttonSize: PropTypes.oneOf(BUTTON_SIZES) };

ReviewAndSubmit.defaultProps = { buttonSize: 'medium' };

export default ReviewAndSubmit;
